// Public BlockNote editor primitive — solo mode, no Yjs collab.
//
// Usage:
//
//   <BlockNoteEditor
//     documentId={account.notes_document_id ?? null}
//     documentKind="account_notes"
//     fallbackText={null}
//     onCreated={(newId) => patchHostWithDocId(newId)}
//   />
//
// The component owns:
//
// - Lazy-loading the heavy ``@blocknote/react`` module (kept out of
//   the main bundle).
// - Fetching ``GET /api/v1/documents/{id}`` when ``documentId`` is
//   set.
// - Autosave on debounced change → ``PATCH /api/v1/documents/{id}``.
// - Document creation on first edit when ``documentId`` is null;
//   ``onCreated`` lets the parent attach the new id to its host
//   record (``account.notes_document_id``, ``commitment.description_document_id``,
//   etc.).
// - Rendering loading / error / readonly states honestly.

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

import {
  createDocument,
  fetchDocument,
  updateDocument,
  type BlockNoteDocument,
} from "./api";


const BlockNoteEditorInner = lazy(() =>
  import("./BlockNoteEditorInner").then((m) => ({
    default: m.BlockNoteEditorInner,
  })),
);


export interface BlockNoteEditorProps {
  /** Existing document id, or ``null`` if no document has been
   *  created yet. */
  documentId: string | null;
  /** Discriminator used when the component creates the document
   *  on first edit. */
  documentKind:
    | "commitment_description"
    | "account_notes"
    | "plan_year_notes";
  /** Plain-text fallback used when the editor is opened with no
   *  stored blocks (e.g. seeded Commitment description). */
  fallbackText?: string | null;
  /** Fires once when the editor lazily creates a document because
   *  the parent didn't have one yet. */
  onCreated?: (documentId: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  /** Debounce window in ms for autosave PATCHes. */
  debounceMs?: number;
  /** Test hook so the autosave path can be exercised
   *  synchronously without waiting on real timers. */
  __testForceImmediateSave?: boolean;
}


type Status = "idle" | "saving" | "saved" | "error";


export function BlockNoteEditor({
  documentId,
  documentKind,
  fallbackText,
  onCreated,
  readOnly,
  placeholder,
  debounceMs = 800,
  __testForceImmediateSave,
}: BlockNoteEditorProps) {
  const [doc, setDoc] = useState<BlockNoteDocument | null>(null);
  const [loading, setLoading] = useState<boolean>(documentId !== null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const localDocumentIdRef = useRef<string | null>(documentId);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCreatedFiredRef = useRef<boolean>(false);

  // Keep the local id in sync if the parent later passes one in.
  useEffect(() => {
    localDocumentIdRef.current = documentId;
  }, [documentId]);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    if (documentId === null) {
      setDoc(null);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    setError(null);
    fetchDocument(documentId)
      .then((d) => {
        if (cancelled) return;
        setDoc(d);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Couldn't load notes.",
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  // Cleanup pending debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const persist = useCallback(
    async (blocks: unknown, text: string) => {
      setStatus("saving");
      try {
        if (localDocumentIdRef.current === null) {
          const created = await createDocument({
            document_kind: documentKind,
            text: text || fallbackText || null,
            blocks_json: { blocks },
          });
          localDocumentIdRef.current = created.document_id;
          setDoc(created);
          if (!onCreatedFiredRef.current && onCreated) {
            onCreatedFiredRef.current = true;
            onCreated(created.document_id);
          }
        } else {
          const updated = await updateDocument(
            localDocumentIdRef.current,
            { blocks_json: { blocks }, blocks_text: text },
          );
          setDoc(updated);
        }
        setStatus("saved");
      } catch (err: unknown) {
        setStatus("error");
        setError(
          err instanceof Error ? err.message : "Couldn't save notes.",
        );
      }
    },
    [documentKind, fallbackText, onCreated],
  );

  const handleEditorChange = useCallback(
    (payload: { blocks: unknown; text: string }) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (__testForceImmediateSave) {
        void persist(payload.blocks, payload.text);
        return;
      }
      timerRef.current = setTimeout(() => {
        void persist(payload.blocks, payload.text);
      }, debounceMs);
    },
    [persist, debounceMs, __testForceImmediateSave],
  );

  if (loading) {
    return (
      <div
        data-testid="blocknote-loading"
        className="rounded-md border border-border-subtle bg-recessed px-3 py-2 text-xs text-text-tertiary"
      >
        Loading notes…
      </div>
    );
  }

  // ``error`` set during load (no doc data) → block the editor;
  // ``error`` set during save (doc data present) → render banner
  // alongside the editor so the user can keep typing.
  if (error && doc === null && documentId !== null) {
    return (
      <div
        data-testid="blocknote-error"
        role="alert"
        className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
      >
        Couldn't load notes. {error}
      </div>
    );
  }

  const initialBlocks = readDocBlocks(doc);

  return (
    <div className="flex flex-col gap-1" data-testid="blocknote-editor">
      <Suspense fallback={
        <div
          data-testid="blocknote-loading"
          className="rounded-md border border-border-subtle bg-recessed px-3 py-2 text-xs text-text-tertiary"
        >
          Loading editor…
        </div>
      }>
        <div
          className="rounded-md border border-border-subtle bg-raised"
          data-placeholder={placeholder}
        >
          <BlockNoteEditorInner
            initialBlocks={initialBlocks}
            fallbackText={fallbackText ?? null}
            readOnly={Boolean(readOnly)}
            onChange={handleEditorChange}
          />
        </div>
      </Suspense>
      <SaveStatusBadge status={status} error={error} />
    </div>
  );
}


function readDocBlocks(doc: BlockNoteDocument | null): unknown {
  if (!doc) return null;
  const raw = doc.blocks_json as unknown;
  if (raw && typeof raw === "object" && "blocks" in (raw as object)) {
    return (raw as { blocks: unknown }).blocks;
  }
  return raw;
}


function SaveStatusBadge({
  status, error,
}: { status: Status; error: string | null }) {
  if (status === "saving") {
    return (
      <span
        data-testid="blocknote-save-status"
        data-status="saving"
        className="text-[11px] text-text-tertiary"
      >
        Saving…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span
        data-testid="blocknote-save-status"
        data-status="saved"
        className="text-[11px] text-text-tertiary"
      >
        Saved
      </span>
    );
  }
  if (status === "error" && error) {
    return (
      <span
        data-testid="blocknote-save-status"
        data-status="error"
        role="alert"
        className="text-[11px] text-ember"
      >
        Couldn't save. {error}
      </span>
    );
  }
  return null;
}
